"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { isTheme, THEME_STORAGE_KEY, type ResolvedTheme, type Theme } from "@/lib/theme";

type ThemeContextValue = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setPreference] = useState<Theme>("system");
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>("dark");
  const preferenceRef = useRef<Theme>("system");

  const apply = useCallback((preference: Theme) => {
    preferenceRef.current = preference;
    const resolved = preference === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
      : preference;
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolved);
    root.dataset.theme = resolved;
    root.style.colorScheme = resolved;
    const color = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (color) color.content = resolved === "dark" ? "#09090b" : "#f9faf6";
    setPreference(preference);
    setResolvedTheme(resolved);
  }, []);

  const setTheme = useCallback((preference: Theme) => {
    if (!isTheme(preference)) return;
    try { localStorage.setItem(THEME_STORAGE_KEY, preference); } catch { /* Optional persistence. */ }
    apply(preference);
  }, [apply]);

  useEffect(() => {
    const saved = () => {
      try {
        const value = localStorage.getItem(THEME_STORAGE_KEY);
        return isTheme(value) ? value : "system";
      } catch { return "system"; }
    };
    apply(saved());
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const systemChange = () => { if (preferenceRef.current === "system") apply("system"); };
    const storageChange = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) apply(saved());
    };
    media.addEventListener("change", systemChange);
    window.addEventListener("storage", storageChange);
    return () => {
      media.removeEventListener("change", systemChange);
      window.removeEventListener("storage", storageChange);
    };
  }, [apply]);

  return <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
}
